import { useState, useEffect, Component } from 'react';
import { useNavigate } from 'react-router-dom';
import { C, FONTS } from '../../tokens/design';
import { useClientProfile, useClientScore, useRecentFindings, useClientAlerts, useScoreHistory, useAttackPaths, useReports, useFrameworks, useAssets, useOnboardingStatus, useScanHistory, useMetricsRollup } from '../../hooks/useClientData';
import { normalizeSeverity } from '../../utils/severity';
import { useAuth } from '../../context/AuthContext';
import { useClient } from '../../context/ClientContext';
import { useUser } from '../../context/UserContext';
import { getImpersonatedMspId, useApiClient } from '../../api/apiClient';
import { relativeTime, exactTime } from '../../utils/time';
import Shell from '../../components/layout/Shell';
import Card, { CardHeader } from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import Skeleton from '../../components/ui/Skeleton';
import PageError from '../../components/ui/PageError';
import ScoreGauge from '../../components/ui/ScoreGauge';
import SeverityBadge from '../../components/badges/SeverityBadge';
import StatusBadge from '../../components/badges/StatusBadge';
import KevBadge from '../../components/badges/KevBadge';
import EpssVelocity from '../../components/badges/EpssVelocity';
import Tag from '../../components/ui/Tag';
import { TH, TR, TD } from '../../components/ui/Table';
import { groupByTitle, targetLabel } from '../../utils/findingTarget';
import { dashboardContractCounts } from '../../utils/metricsRollup';
import { dashboardOsintPresentation } from '../../utils/osintCoverage';
import { canonicalizeAssets } from './Assets';

class DashboardErrorBoundary extends Component {
  state = { error: null };
  static getDerivedStateFromError(error) { return { error }; }
  componentDidCatch(err, info) { console.error('[Dashboard] Render crash:', err, info.componentStack); }
  render() {
    if (this.state.error) return (
      <div style={{ padding: 40, textAlign: 'center' }}>
        <div style={{ fontSize: 14, fontWeight: 700, color: C.red, marginBottom: 8 }}>Dashboard failed to render</div>
        <div style={{ fontSize: 11, color: C.t2, fontFamily: 'monospace', marginBottom: 12 }}>{String(this.state.error)}</div>
        <button onClick={() => this.setState({ error: null })} style={{ padding: '6px 16px', background: C.sky, color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: 11 }}>Retry</button>
      </div>
    );
    return this.props.children;
  }
}

function Sparkline({ data, color, width = 80, height = 24 }) {
  if (!Array.isArray(data) || data.length === 0) return null;
  const nums = data.map(v => Number(v) || 0);
  const min = Math.min(...nums) - 2, max = Math.max(...nums) + 2;
  const range = max - min || 1;
  const xDivisor = nums.length > 1 ? nums.length - 1 : 1;
  const pts = nums.map((v, i) => `${(i / xDivisor) * width},${height - ((v - min) / range) * height}`).join(' ');
  return (
    <svg width={width} height={height} style={{ display: 'block' }}>
      <polyline points={pts} fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function frameworkMeta(name) {
  const raw = String(name || '').trim();
  const key = raw.toLowerCase().replace(/[^a-z0-9]/g, '');
  if (key.includes('nist800171')) return { mark: 'NIST', label: 'NIST 800-171 R2' };
  if (key.includes('nist80053')) return { mark: 'NIST', label: 'NIST 800-53 Rev. 5' };
  if (key.includes('nistcsf')) return { mark: 'NIST', label: 'NIST CSF 2.0' };
  if (key === 'nist' || key.includes('nist')) return { mark: 'NIST', label: raw.includes('800') ? raw : 'NIST 800-53 Rev. 5' };
  if (key.includes('cis')) return { mark: 'CIS', label: 'CIS Controls v8.1' };
  if (key.includes('soc2') || key === 'soc') return { mark: 'SOC 2', label: 'SOC 2 Type II' };
  if (key.includes('hipaa')) return { mark: 'HIPAA', label: 'HIPAA Security Rule' };
  if (key.includes('pci')) return { mark: 'PCI', label: 'PCI DSS v4.0.1' };
  if (key.includes('iso')) return { mark: 'ISO', label: 'ISO/IEC 27001:2022' };
  if (key.includes('cmmc2') || key.includes('level2')) return { mark: 'CMMC', label: 'CMMC Level 2 v2.0' };
  if (key.includes('cmmc1') || key.includes('level1')) return { mark: 'CMMC', label: 'CMMC Level 1 v2.0' };
  if (key.includes('cmmc')) return { mark: 'CMMC', label: 'CMMC v2.0' };
  const label = raw
    ? raw.replace(/[_-]+/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
    : 'Framework';
  return { mark: label.split(' ')[0].toUpperCase(), label };
}

function dateValue(item, fields) {
  for (const field of fields) {
    const value = item?.[field];
    if (!value) continue;
    const time = new Date(value).getTime();
    if (!Number.isNaN(time)) return time;
  }
  return null;
}

function recommendationScore(finding) {
  const sev = normalizeSeverity(finding?.sev ?? finding?.severity).toLowerCase();
  const severityWeight = { critical: 10, high: 7, medium: 4, low: 2, info: 1 }[sev] ?? 1;
  const cvss = Number(finding?.cvss ?? finding?.cvssScore ?? finding?.cvssV3 ?? finding?.details?.cvss ?? severityWeight) || severityWeight;
  const epssRaw = Number(finding?.epss ?? finding?.ep ?? finding?.epssScore ?? 0) || 0;
  const epss = epssRaw > 1 ? epssRaw / 100 : epssRaw;
  const text = `${finding?.title ?? ''} ${finding?.asset ?? ''} ${finding?.resource ?? ''} ${finding?.remediation ?? ''}`.toLowerCase();
  const assetCriticality = /(domain controller|domain admin|admin|prod|production|database|payment|pii|customer|identity|iam|root|vpn|firewall)/.test(text) ? 1.35 : 1;
  const easyFix = /(enable|disable|rotate|restrict|remove public|public access|patch|update|encrypt|mfa|close port)/.test(text) ? 1.18 : 1;
  return Number((cvss * (1 + epss) * assetCriticality * easyFix).toFixed(1));
}

function recommendationReason(finding, score) {
  const text = `${finding?.title ?? ''} ${finding?.asset ?? ''} ${finding?.remediation ?? ''}`.toLowerCase();
  const reasons = [`Impact ${score}`];
  if (/(domain controller|domain admin|admin|prod|production|database|payment|pii|customer|identity|iam|root)/.test(text)) reasons.push('critical asset');
  if (/(enable|disable|rotate|restrict|remove public|public access|patch|update|encrypt|mfa|close port)/.test(text)) reasons.push('fast remediation');
  if (Number(finding?.epss ?? finding?.ep ?? 0) > 0) reasons.push('exploit likelihood');
  return reasons.join(' - ');
}

function valueAt(obj, ...keys) {
  if (!obj || typeof obj !== 'object') return undefined;
  for (const key of keys) {
    if (obj[key] !== undefined && obj[key] !== null) return obj[key];
    const found = Object.keys(obj).find(k => k.toLowerCase() === String(key).toLowerCase());
    if (found && obj[found] !== undefined && obj[found] !== null) return obj[found];
  }
  return undefined;
}

function numberAt(obj, fallback = 0, ...keys) {
  const value = valueAt(obj, ...keys);
  const number = Number(value);
  return Number.isFinite(number) ? number : fallback;
}

const DASHBOARD_ASSET_TYPE_MAP = {
  externalip: 1,
  external_ip: 1,
  internalhost: 2,
  internal_host: 2,
  domain: 3,
  cloudresource: 4,
  cloud_resource: 4,
  user: 5,
  account: 5,
};

function dashboardAssetType(asset) {
  if (typeof asset?.assetType === 'number') return asset.assetType;
  return DASHBOARD_ASSET_TYPE_MAP[String(asset?.assetType ?? '').toLowerCase()] ?? 0;
}

export function summarizeDashboardAssets(assets, rollupCounts = {}) {
  const assetList = Array.isArray(assets) ? assets : [];
  const local = {
    total: assetList.length,
    externalIps: assetList.filter(a => dashboardAssetType(a) === 1).length,
    internalHosts: assetList.filter(a => dashboardAssetType(a) === 2).length,
    domains: assetList.filter(a => dashboardAssetType(a) === 3).length,
    cloudResources: assetList.filter(a => dashboardAssetType(a) === 4).length,
    accounts: assetList.filter(a => dashboardAssetType(a) === 5).length,
  };
  const countAt = (localValue, ...keys) => {
    const rollupValue = numberAt(rollupCounts, null, ...keys);
    return rollupValue > 0 || localValue === 0 ? rollupValue ?? 0 : localValue;
  };

  return {
    total: countAt(local.total, 'assets', 'total'),
    externalIps: countAt(local.externalIps, 'externalIps'),
    internalHosts: countAt(local.internalHosts, 'internalHosts'),
    domains: countAt(local.domains, 'domains'),
    cloudResources: countAt(local.cloudResources, 'cloudResources'),
    accounts: countAt(local.accounts, 'accounts'),
  };
}

function friendlyFindingSource(finding) {
  const raw = String(finding?.asset || finding?.module || finding?.fingerprint || '').trim();
  const key = raw.toLowerCase();
  if (!raw) return 'Managed asset';
  if (key.includes('nessus')) return 'Nessus scanner';
  if (key.includes('nuclei') || key.includes('dast')) return 'Web application scan';
  if (key.includes('cloud') || key.includes('cspm') || key.includes('aws') || key.includes('azure') || key.includes('m365')) return 'Cloud CSPM';
  if (key.includes('osint') || key.includes('breach') || key.includes('dehashed') || key.includes('hibp')) return 'External intelligence';
  if (key.includes('sdd') || key.includes('sensitive')) return 'Sensitive data discovery';
  if (key.includes('ad-') || key.includes('active-directory')) return 'Active Directory assessment';
  if (key.includes('msp-demo') || key.includes('msp-lived-in') || key.includes('msp-density')) return 'Managed detection baseline';
  return raw;
}

function dedupeReports(reports) {
  const byKey = new Map();
  for (const report of reports.filter(Boolean)) {
    const type = String(report.reportType ?? report.type ?? 'monthly').toLowerCase().replace(/\d+/g, '');
    const name = String(report.title ?? report.name ?? `${type} report`).toLowerCase().replace(/\s+/g, ' ').trim();
    const generated = report.generatedAt ?? report.gen ?? report.createdAt ?? '';
    const generatedTime = generated ? new Date(generated).getTime() : null;
    const monthKey = generatedTime && !Number.isNaN(generatedTime) ? new Date(generatedTime).toISOString().slice(0, 7) : '';
    const key = `${type}|${name}|${monthKey}`;
    const current = byKey.get(key);
    const currentTime = current ? dateValue(current, ['generatedAt', 'gen', 'createdAt']) ?? 0 : 0;
    const nextTime = dateValue(report, ['generatedAt', 'gen', 'createdAt']) ?? 0;
    if (!current || nextTime >= currentTime) byKey.set(key, report);
  }
  return [...byKey.values()].sort((a, b) => (dateValue(b, ['generatedAt', 'gen', 'createdAt']) ?? 0) - (dateValue(a, ['generatedAt', 'gen', 'createdAt']) ?? 0));
}

function ScoreHistoryChart({ SCORE_HISTORY }) {
  const [hover, setHover] = useState(null);
  if (!Array.isArray(SCORE_HISTORY) || SCORE_HISTORY.length === 0) return (
    <Card>
      <CardHeader title="Security Score Trend" />
      <div style={{ padding: '32px 16px', textAlign: 'center', color: C.t3, fontFamily: FONTS.mono, fontSize: 12 }}>
        No score history available yet. Scores will appear after scans complete.
      </div>
    </Card>
  );

  // Normalize and deduplicate: one point per calendar day (keep latest)
  const rawPoints = SCORE_HISTORY.filter(Boolean).map(d => {
    const dateStr = d?.scoredAt ?? d?.calculatedAt ?? d?.score?.scoredAt ?? d?.createdAt ?? null;
    return {
      score: d?.overallScore ?? d?.scoreValue ?? d?.score?.overallScore ?? d?.org ?? 0,
      date: dateStr,
      label: dateStr ? new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : (d?.label ?? ''),
    };
  });
  const dayMap = new Map();
  for (const p of rawPoints) {
    const key = p.label || String(dayMap.size);
    dayMap.set(key, p); // last entry per day wins
  }
  // Sort chronologically so oldest renders leftmost, newest rightmost.
  const points = [...dayMap.values()].sort((a, b) => {
    const da = a.date ? new Date(a.date).getTime() : 0;
    const db = b.date ? new Date(b.date).getTime() : 0;
    return da - db;
  });

  const W = 560, H = 160, PAD = { t: 16, r: 16, b: 28, l: 32 };
  const cW = W - PAD.l - PAD.r, cH = H - PAD.t - PAD.b;
  const allVals = points.map(d => d.score);
  const last = points[points.length - 1];
  const scoreRange = allVals.length > 0 ? Math.max(...allVals) - Math.min(...allVals) : 0;
  if (points.length < 2 || scoreRange < 3) return (
    <Card>
      <CardHeader title="Security Score Trend" action={
        last ? <span style={{ fontSize: 11, fontWeight: 700, color: C.sky, fontFamily: FONTS.mono }}>{last.score} / 100</span> : null
      } />
      <div style={{ padding: '20px 18px 18px' }}>
        <div style={{ height: 92, border: `1px solid ${C.b1}`, borderRadius: 6, background: C.s2, position: 'relative', overflow: 'hidden', marginBottom: 12 }}>
          <div style={{ position: 'absolute', left: 16, right: 16, top: '50%', borderTop: `2px solid ${C.sky}55` }} />
          <div style={{ position: 'absolute', left: 18, top: 'calc(50% - 5px)', width: 10, height: 10, borderRadius: '50%', background: C.sky, boxShadow: `0 0 0 4px ${C.sky}18` }} />
          <div style={{ position: 'absolute', right: 16, top: 14, fontSize: 9, color: C.t3, fontFamily: FONTS.mono }}>0-100 scale</div>
        </div>
        <div style={{ fontSize: 13, fontWeight: 700, color: C.t1, marginBottom: 4 }}>Score baseline established.</div>
        <div style={{ fontSize: 11, color: C.t2, lineHeight: 1.5 }}>Trend will populate after the next scan. This keeps the chart from implying movement before there is enough history.</div>
      </div>
    </Card>
  );
  const minV = Math.max(0, Math.min(...allVals) - 5), maxV = Math.min(100, Math.max(...allVals) + 5);
  const range = maxV - minV || 1;
  const xDivisor = points.length > 1 ? points.length - 1 : 1;
  const xPos = i => PAD.l + (i / xDivisor) * cW;
  const yPos = v => PAD.t + cH - ((v - minV) / range) * cH;
  const linePath = points.map((d, i) => `${i === 0 ? 'M' : 'L'}${xPos(i).toFixed(1)},${yPos(d.score).toFixed(1)}`).join(' ');
  const areaPath = `${linePath} L${xPos(points.length - 1).toFixed(1)},${(PAD.t + cH).toFixed(1)} L${PAD.l},${(PAD.t + cH).toFixed(1)} Z`;

  return (
    <Card>
      <CardHeader title="Security Score Trend" action={
        <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
          <div style={{ width: 10, height: 2, background: C.sky, borderRadius: 1 }} />
          <span style={{ fontSize: 10, color: C.t2, fontFamily: FONTS.mono }}>Score</span>
          <span style={{ fontSize: 11, fontWeight: 700, color: C.sky, fontFamily: FONTS.mono }}>{last.score}</span>
        </div>
      } />
      <div style={{ padding: '12px 16px 8px', position: 'relative' }}>
        <svg width="100%" viewBox={`0 0 ${W} ${H}`} style={{ overflow: 'visible', display: 'block' }}>
          <defs>
            <linearGradient id="scoreGrad" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor={C.sky} stopOpacity="0.18" /><stop offset="100%" stopColor={C.sky} stopOpacity="0" /></linearGradient>
          </defs>
          {[0, .25, .5, .75, 1].map(t => {
            const y = PAD.t + t * cH;
            const val = Math.round(maxV - (t * (maxV - minV)));
            return <g key={t}><line x1={PAD.l} y1={y} x2={W - PAD.r} y2={y} stroke={C.b1} strokeWidth="1" /><text x={PAD.l - 4} y={y + 4} textAnchor="end" fontSize="8" fill={C.t2} fontFamily={FONTS.mono}>{val}</text></g>;
          })}
          <path d={areaPath} fill="url(#scoreGrad)" />
          <path d={linePath} fill="none" stroke={C.sky} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          {points.map((d, i) => {
            const x = xPos(i), y1 = yPos(d.score), isH = hover === i;
            return (
              <g key={i}>
                <rect x={x - 18} y={PAD.t} width={36} height={cH} fill="transparent" style={{ cursor: 'pointer' }} onMouseEnter={() => setHover(i)} onMouseLeave={() => setHover(null)} />
                {isH && <line x1={x} y1={PAD.t} x2={x} y2={PAD.t + cH} stroke={C.b1} strokeWidth="1" strokeDasharray="3 2" />}
                <text x={x} y={PAD.t + cH + 14} textAnchor="middle" fontSize="8" fill={isH ? C.t1 : C.t2} fontFamily={FONTS.mono}>{d.label}</text>
                <circle cx={x} cy={y1} r={isH ? 4 : 2.5} fill={C.sky} stroke={C.s1} strokeWidth={isH ? 2 : 1} />
                {isH && <g>
                  <rect x={x - 30} y={y1 - 22} width={60} height={16} rx="4" fill={C.b1} stroke={C.b2} strokeWidth="1" />
                  <text x={x} y={y1 - 11} textAnchor="middle" fontSize="9" fill={C.sky} fontFamily={FONTS.mono} fontWeight="700">{d.score}</text>
                </g>}
              </g>
            );
          })}
        </svg>
      </div>
    </Card>
  );
}

function OnboardingChecklist({ status, navigate, scanCount, clientId }) {
  const [dismissed, setDismissed] = useState(() => {
    try { return localStorage.getItem(`checklist-dismissed-${clientId}`) === '1'; } catch { return false; }
  });
  if (!status || typeof status !== 'object' || dismissed) return null;

  const STEP_META = {
    deployScanAgent:      { label: 'Deploy scan agent', link: null, fallbackSub: 'Download and install the Sentinel agent' },
    configureCredentials: { label: 'Configure credentials', link: '/client/secrets', fallbackSub: 'Add scan credentials for authenticated scanning' },
    discoverAssets:       { label: 'Discover assets', link: '/client/assets', fallbackSub: 'Run initial scan to discover your attack surface' },
    completeFirstScan:    { label: 'Complete first scan', link: '/client/scans', fallbackSub: 'Run a full vulnerability scan' },
    setupNotifications:   { label: 'Set up notifications', link: '/account/alerts', fallbackSub: 'Configure alert delivery for critical findings' },
    scheduleScan:         { label: 'Schedule recurring scans', link: '/client/scan-schedule', fallbackSub: 'Automate scanning on a regular cadence' },
  };

  // Build steps from API response, preserving order
  const stepKeys = ['deployScanAgent', 'configureCredentials', 'discoverAssets', 'completeFirstScan', 'setupNotifications', 'scheduleScan'];
  const steps = stepKeys.map(key => {
    const apiStep = status[key] ?? (Array.isArray(status.steps) ? status.steps.find(s => s.key === key) : undefined);
    const meta = STEP_META[key];
    const done = apiStep?.completed ?? apiStep === true ?? false;
    const detail = apiStep?.detail ?? apiStep?.detailText ?? null;
    return { key, label: meta.label, sub: detail || meta.fallbackSub, link: meta.link, done };
  });

  const completed = status.completedSteps ?? steps.filter(s => s.done).length;
  const total = status.totalSteps ?? steps.length;
  const pct = total > 0 ? Math.round((completed / total) * 100) : 0;

  if (pct >= 100) return null;
  // Auto-hide when client has meaningful progress and scans
  if (completed >= 2 && scanCount > 0) return null;

  const handleDismiss = () => {
    try { localStorage.setItem(`checklist-dismissed-${clientId}`, '1'); } catch {}
    setDismissed(true);
  };

  return (
    <Card style={{ padding: 20, marginBottom: 14, borderLeft: `3px solid ${C.sky}` }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <div>
          <div style={{ fontSize: 14, fontWeight: 700, fontFamily: FONTS.sans, color: C.t1 }}>Getting Started</div>
          <div style={{ fontSize: 11, color: C.t2, fontFamily: FONTS.mono, marginTop: 2 }}>{completed} of {total} steps complete</div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <div style={{ width: 100, height: 6, background: C.b1, borderRadius: 3, overflow: 'hidden' }}>
            <div style={{ height: '100%', width: `${pct}%`, background: C.sky, borderRadius: 3, transition: 'width 0.3s' }} />
          </div>
          <span style={{ fontSize: 11, fontWeight: 700, color: C.sky, fontFamily: FONTS.mono }}>{pct}%</span>
          <button onClick={handleDismiss} title="Dismiss checklist" style={{ background: 'none', border: 'none', color: C.t3, cursor: 'pointer', fontSize: 14, padding: '0 2px', lineHeight: 1 }}>{'\u00D7'}</button>
        </div>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
        {steps.map(step => (
          <div
            key={step.key}
            onClick={() => !step.done && step.link && navigate(step.link)}
            style={{
              display: 'flex', alignItems: 'flex-start', gap: 8,
              padding: '8px 10px', borderRadius: 6,
              background: step.done ? `${C.grn}08` : C.s2,
              border: `1px solid ${step.done ? C.grn + '33' : C.b1}`,
              cursor: !step.done && step.link ? 'pointer' : 'default',
              opacity: step.done ? 0.7 : 1,
            }}
          >
            <span style={{ fontSize: 14, marginTop: 1, flexShrink: 0 }}>{step.done ? '\u2705' : '\u2610'}</span>
            <div>
              <div style={{ fontSize: 11, fontWeight: 600, color: step.done ? C.grn : C.t1, fontFamily: FONTS.sans }}>{step.label}</div>
              <div style={{ fontSize: 9, color: step.done ? C.grn : C.t3, fontFamily: FONTS.mono, marginTop: 1 }}>{step.sub}</div>
            </div>
          </div>
        ))}
      </div>
    </Card>
  );
}

export default function Dashboard() {
  return <DashboardErrorBoundary><DashboardInner /></DashboardErrorBoundary>;
}

function DashboardInner() {
  const navigate = useNavigate();
  const { getAccessToken } = useAuth();
  const { clientId, setClientId, clientName: ctxClientName } = useClient();
  const { isMspUser, isPlatformAdmin } = useUser();
  const api = useApiClient();
  const [secondaryDataReady, setSecondaryDataReady] = useState(false);

  // Platform admins / MSP users with no active client: fetch first client from API
  useEffect(() => {
    if (!clientId && (isPlatformAdmin || isMspUser)) {
      api.get('/clients')
        .then(clients => {
          if (Array.isArray(clients) && clients.length > 0) {
            setClientId(clients[0].clientId);
          }
        })
        .catch(err => console.error('[Dashboard] Failed to fetch default client:', err));
    }
  }, [clientId, isPlatformAdmin, isMspUser]);

  useEffect(() => {
    setSecondaryDataReady(false);
    if (!clientId) return undefined;
    const timer = window.setTimeout(() => setSecondaryDataReady(true), 1400);
    return () => window.clearTimeout(timer);
  }, [clientId]);

  const { data: _client, error: clientError } = useClientProfile();
  const { data: SCORE_DATA, error: scoreError } = useClientScore();
  const { data: _findings, error: findingsError } = useRecentFindings(10);
  const { data: _alerts, error: alertsError } = useClientAlerts();
  const { data: _scoreHistory } = useScoreHistory();
  const { data: _attackPaths } = useAttackPaths(secondaryDataReady);
  const { data: _reports } = useReports(secondaryDataReady);
  const { data: _frameworks } = useFrameworks(secondaryDataReady);
  const { data: _assets, loading: assetsLoading } = useAssets(secondaryDataReady);
  const { data: onboardingStatus } = useOnboardingStatus(secondaryDataReady);
  const { data: _scanHistory } = useScanHistory(secondaryDataReady);
  const { data: metricsRollup, loading: metricsLoading, error: metricsError } = useMetricsRollup();
  const dashError = clientError || scoreError || findingsError || alertsError || metricsError;
  const CLIENT = _client ?? null;
  const FINDINGS = Array.isArray(_findings) ? _findings : [];
  const ALERTS = Array.isArray(_alerts) ? _alerts : [];
  const SCORE_HISTORY = Array.isArray(_scoreHistory) ? _scoreHistory : [];
  const ATTACK_PATHS = Array.isArray(_attackPaths) ? _attackPaths : [];
  const REPORTS = Array.isArray(_reports) ? _reports : [];
  const FRAMEWORKS = Array.isArray(_frameworks) ? _frameworks : [];
  const ASSETS = Array.isArray(_assets) ? _assets : [];
  const SCAN_HISTORY_RAW = Array.isArray(_scanHistory) ? _scanHistory : [];
  const [initialLoading, setLoading] = useState(true);
  const [downloadingPkg, setDownloadingPkg] = useState(false);
  const [downloadingEndpointPkg, setDownloadingEndpointPkg] = useState(false);
  const [downloadError, setDownloadError] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [scanMessage, setScanMessage] = useState('');
  const [showScoreMethodology, setShowScoreMethodology] = useState(false);
  useEffect(() => { const t = setTimeout(() => setLoading(false), 700); return () => clearTimeout(t); }, []);
  const loading = initialLoading;

  const handleDownloadEndpointPackage = async () => {
    if (!clientId) return;
    setDownloadingEndpointPkg(true);
    setDownloadError(null);
    try {
      const token = await getAccessToken();
      const headers = {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      };
      const impersonatedMspId = getImpersonatedMspId();
      if (impersonatedMspId) headers['X-Act-As-Msp'] = impersonatedMspId;
      const baseUrl = import.meta.env.VITE_API_BASE_URL || '';
      const res = await fetch(`${baseUrl}/clients/${clientId}/connectors/deployment-package`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ connectorGroup: 'pilot' }),
      });
      if (!res.ok) {
        const responseText = await res.text().catch(() => '');
        let message = responseText;
        try {
          const parsed = JSON.parse(responseText);
          const detail = parsed?.error?.message || parsed?.error || parsed?.message || responseText;
          message = typeof detail === 'string' ? detail : JSON.stringify(detail);
        } catch {
          // Preserve plain-text API errors.
        }
        if (message.includes('WireGuard connector settings must be saved')) {
          throw new Error('WireGuard connector settings are missing. Save them on the MSP Dashboard, then retry.');
        }
        throw new Error(message || `Endpoint package generation failed (${res.status})`);
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `sentinel-endpoint-connector-${clientId}-pilot.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      setDownloadError(err.message || 'Endpoint package download failed.');
    } finally {
      setDownloadingEndpointPkg(false);
    }
  };

  const handleDownloadPackage = async () => {
    if (!clientId) return;
    setDownloadingPkg(true);
    try {
      const token = await getAccessToken();
      const baseUrl = import.meta.env.VITE_API_BASE_URL || '';
      const res = await fetch(`${baseUrl}/clients/${clientId}/onboarding-package`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Download failed');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `sentinel-setup-${clientId}.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      setDownloadError(err.message);
    } finally {
      setDownloadingPkg(false);
    }
  };

  const handleTriggerScan = async () => {
    setScanning(true);
    setScanMessage('');
    try {
      await api.post(`/clients/${clientId}/scans`, { profileName: 'full' });
      setScanMessage('Scan queued successfully \u2014 results will appear in 2-3 minutes.');
      setTimeout(() => setScanMessage(prev => prev && !prev.startsWith('Failed') ? '' : prev), 8000);
      // Poll for scan completion
      const pollInterval = setInterval(async () => {
        try {
          const history = await api.get(`/clients/${clientId}/scan-history?pageSize=1`);
          const latest = Array.isArray(history) ? history[0] : history?.items?.[0];
          if (latest && latest.status !== 'Running' && latest.status !== 'Queued') {
            clearInterval(pollInterval);
            setScanning(false);
          }
        } catch {
          clearInterval(pollInterval);
          setScanning(false);
        }
      }, 30000);
      // Safety timeout: stop polling after 10 minutes
      setTimeout(() => { clearInterval(pollInterval); setScanning(false); }, 600000);
    } catch (err) {
      setScanMessage(`Failed to queue scan: ${err.message}`);
      setScanning(false);
    }
  };

  const dashboardCounts = dashboardContractCounts(metricsRollup);
  const visibleFindings = FINDINGS.filter(f => f.status !== 'suppressed' && f.status !== 'possibly_remediated');
  const visibleOpenFindings = visibleFindings.filter(f => ['open', 'reopened', 'remediation_claimed'].includes(f.status));
  const visibleSeverity = visibleFindings.reduce((acc, f) => {
    acc[f.sev] = (acc[f.sev] ?? 0) + 1;
    return acc;
  }, { critical: 0, high: 0, medium: 0, low: 0, info: 0 });
  const visibleOpenCriticalHigh = visibleOpenFindings.filter(f => f.sev === 'critical' || f.sev === 'high').length;
  const sevCounts = {
    critical: Math.max(visibleSeverity.critical, dashboardCounts.critical),
    high: Math.max(visibleSeverity.high, dashboardCounts.high),
    medium: Math.max(visibleSeverity.medium, dashboardCounts.medium),
    low: Math.max(visibleSeverity.low, dashboardCounts.low),
    info: Math.max(visibleSeverity.info, dashboardCounts.info),
  };
  const openCriticalHigh = Math.max(visibleOpenCriticalHigh, dashboardCounts.openCriticalHigh);
  const totalFindings = Math.max(visibleFindings.length, dashboardCounts.totalFindings);
  const openCount = Math.max(visibleOpenFindings.length, dashboardCounts.openFindings);
  const resolvedCount = dashboardCounts.resolvedFindings;
  const acceptedCount = dashboardCounts.acceptedRiskFindings;
  const breachRecords = dashboardCounts.breachRecords;
  const exposedCredentials = dashboardCounts.exposedCredentials;
  const exposedServices = dashboardCounts.exposedServices;
  const totalOsintFindings = dashboardCounts.totalOsintFindings;
  const osintPresentation = dashboardOsintPresentation({
    breachRecords,
    exposedCredentials,
    exposedServices,
    totalOsintFindings,
    coverageDiagnostics: dashboardCounts.osintCoverageDiagnostics,
    coveragePending: metricsLoading,
    coverageError: Boolean(metricsError),
  });
  const sensitiveDataRollup = metricsRollup?.modules?.sdd ?? metricsRollup?.modules?.sensitiveData ?? {};
  const sensitiveDataFindings = sensitiveDataRollup.groupCount || sensitiveDataRollup.visibleGroups || dashboardCounts.sensitiveDataFindings;
  const openSensitiveDataFindings = sensitiveDataRollup.openGroupCount || sensitiveDataRollup.open || dashboardCounts.openSensitiveDataFindings;

  // Asset counts: handle both int and string assetType from backend, with list fallback
  // so the dashboard never shows an empty attack surface while inventory is populated.
  const rawAssetList = Array.isArray(ASSETS) ? ASSETS : [];
  const assetList = canonicalizeAssets(rawAssetList);
  const assetRollup = summarizeDashboardAssets(assetList, assetsLoading ? {
    total: dashboardCounts.assets,
    assets: dashboardCounts.assets,
    externalIps: dashboardCounts.externalIps,
    internalHosts: dashboardCounts.internalHosts,
    domains: dashboardCounts.domains,
    cloudResources: dashboardCounts.cloudResources,
    accounts: dashboardCounts.accounts,
  } : {});
  const totalAssets = assetRollup.total;
  const assetHosts = assetRollup.externalIps + assetRollup.internalHosts;
  const assetDomains = assetRollup.domains;
  const assetCloud = assetRollup.cloudResources;
  const assetAccounts = assetRollup.accounts;
  const weekAgo = Date.now() - (7 * 24 * 60 * 60 * 1000);
  const assetObservedAt = (a) => dateValue(a, ['firstSeenAt', 'firstSeen', 'discoveredAt', 'createdAt', 'lastSeenAt', 'lastSeen', 'updatedAt']);
  const hasAssetDateSignals = assetList.some(a => assetObservedAt(a));
  const recentAssetCount = (predicate) => assetList.filter(a => predicate(a) && (assetObservedAt(a) ?? 0) >= weekAgo).length;
  const surfaceSignal = (count, recent) => {
    if (recent > 0) return { text: `+${recent} new this week`, color: C.grn };
    if (count === 0) return { text: 'Not observed', color: C.t3 };
    return { text: hasAssetDateSignals ? 'No new this week' : 'Baseline captured', color: C.t3 };
  };
  const categorizedAssetTotal = assetHosts + assetDomains + assetCloud + assetAccounts;
  const attackSurfaceRows = [
    ...(categorizedAssetTotal === 0 && totalAssets > 0
      ? [{ l: 'Assets in Inventory', n: totalAssets, c: C.t1, signal: { text: 'Inventory synchronized', color: C.grn } }]
      : []),
    { l: 'External IPs', n: assetRollup.externalIps, c: assetRollup.externalIps > 0 ? C.t1 : C.t3, signal: surfaceSignal(assetRollup.externalIps, recentAssetCount(a => dashboardAssetType(a) === 1)) },
    { l: 'Internal Hosts', n: assetRollup.internalHosts, c: assetRollup.internalHosts > 0 ? C.t1 : C.t3, signal: surfaceSignal(assetRollup.internalHosts, recentAssetCount(a => dashboardAssetType(a) === 2)) },
    { l: 'Domains', n: assetDomains, c: assetDomains > 0 ? C.t1 : C.t3, signal: surfaceSignal(assetDomains, recentAssetCount(a => dashboardAssetType(a) === 3)) },
    { l: 'Cloud Resources', n: assetCloud, c: assetCloud > 0 ? C.t1 : C.t3, signal: surfaceSignal(assetCloud, recentAssetCount(a => dashboardAssetType(a) === 4)) },
    { l: 'Accounts', n: assetAccounts, c: assetAccounts > 0 ? C.t1 : C.t3, signal: surfaceSignal(assetAccounts, recentAssetCount(a => dashboardAssetType(a) === 5)) },
    { l: 'Exposed Services (OSINT)', n: osintPresentation.exposedServices.value, c: osintPresentation.coverageIncomplete ? C.amb : exposedServices > 0 ? C.org : C.t3, signal: { text: osintPresentation.coverageIncomplete ? 'Coverage incomplete' : exposedServices > 0 ? 'Changed externally' : 'No signals', color: osintPresentation.coverageIncomplete || exposedServices > 0 ? C.amb : C.t3 } },
  ];

  const COMP = FRAMEWORKS.filter(Boolean).map(f => {
    const labelSource = f.displayName ?? f.name ?? f.canonicalName ?? '';
    return {
      n: f.id ?? labelSource,
      ...frameworkMeta(labelSource),
      s: f.score ?? null,
      d: f.delta ?? 0,
      c: f.controlsTotal ?? f.total ?? 0,
      p: f.controlsPassed ?? f.pass ?? 0,
      status: f.status ?? 'active',
    };
  });
  const dedupedReports = dedupeReports(REPORTS);
  const qbrReports = dedupedReports.filter(r => String(r.reportType ?? r.type ?? '').toLowerCase().replace(/\d+/g, '') === 'qbr');
  const latestQbr = qbrReports[0] ?? null;
  const clientName = CLIENT?.name ?? 'Loading...';
  const scanList = Array.isArray(SCAN_HISTORY_RAW) ? SCAN_HISTORY_RAW : [];
  const latestScan = scanList[0];
  const clientLastScan = latestScan?.completedAt ?? latestScan?.createdAt ?? CLIENT?.lastScanAt ?? CLIENT?.lastScan ?? null;
  const rawScore = SCORE_DATA?.overallScore ?? SCORE_DATA?.scoreValue ?? SCORE_DATA?.score ?? CLIENT?.score ?? null;
  const history = SCORE_HISTORY ?? [];
  const latestHistoryPoint = [...history].filter(Boolean).sort((a, b) => {
    const at = dateValue(a, ['scoredAt', 'calculatedAt', 'createdAt']) ?? 0;
    const bt = dateValue(b, ['scoredAt', 'calculatedAt', 'createdAt']) ?? 0;
    return bt - at;
  })[0];
  const historyScore = latestHistoryPoint?.overallScore ?? latestHistoryPoint?.scoreValue ?? latestHistoryPoint?.score?.overallScore ?? latestHistoryPoint?.org ?? null;

  // Fallback: if score is null/0, check most recent scan for context
  const lastScanEntry = Array.isArray(SCAN_HISTORY_RAW) ? SCAN_HISTORY_RAW[0] : null;
  const lastScanFindingsCount = lastScanEntry?.findingsCount ?? lastScanEntry?.totalFindings ?? null;
  const lastScanTime = lastScanEntry?.createdAt ?? lastScanEntry?.completedAt ?? null;
  const clientScore = rawScore > 0 ? rawScore : historyScore > 0 ? historyScore : null;
  const scoreLabel = clientScore == null ? 'Pending' : clientScore >= 80 ? 'Healthy' : clientScore >= 60 ? 'Fair' : 'At Risk';
  const scoreColor = clientScore == null ? C.t3 : clientScore >= 80 ? C.grn : clientScore >= 60 ? C.amb : C.red;
  const scoreCalcAt = SCORE_DATA?.calculatedAt ?? null;
  const scoreFallbackInfo = clientScore == null && lastScanEntry
    ? `Last scan ${lastScanTime ? relativeTime(lastScanTime) : 'unknown'} / ${lastScanFindingsCount ?? 0} findings`
    : null;

  // Score trend from history
  const latestScore = history.length > 0 ? (history[history.length - 1]?.scoreValue ?? history[history.length - 1]?.org) : undefined;
  const previousScore = history.length > 1 ? (history[history.length - 2]?.scoreValue ?? history[history.length - 2]?.org) : undefined;
  const scoreTrend = (latestScore != null && previousScore != null) ? latestScore - previousScore : 0;
  const trendArrow = scoreTrend > 0 ? '\u25B2' : scoreTrend < 0 ? '\u25BC' : '\u2192';
  const trendColor = scoreTrend > 0 ? C.grn : scoreTrend < 0 ? C.red : C.t3;

  return (
    <Shell title="Security Dashboard" sub={clientLastScan ? `${clientName} / Last scanned ${relativeTime(clientLastScan)}` : clientName}>
      {dashError && <PageError error={dashError} />}
      {downloadError && (
        <div style={{ marginBottom: 12, padding: '8px 14px', borderRadius: 6, fontSize: 11, fontFamily: FONTS.mono, background: C.redBg, border: `1px solid ${C.red}33`, color: C.red, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>Download failed: {downloadError}</span>
          <button onClick={() => setDownloadError(null)} style={{ background: 'none', border: 'none', color: C.red, cursor: 'pointer', fontSize: 14 }}>{'\u2715'}</button>
        </div>
      )}
      {isMspUser && (
        <div style={{ marginBottom: 8, display: 'flex', alignItems: 'center', gap: 12 }}>
          <button onClick={() => navigate('/msp/clients')} style={{ background: 'none', border: 'none', color: C.sky, cursor: 'pointer', fontSize: 11, fontFamily: FONTS.mono, padding: 0 }}>
            {'\u2190'} Back to Clients
          </button>
          {(ctxClientName || clientName) && (
            <span style={{ fontSize: 11, color: C.t2, fontFamily: FONTS.mono }}>
              Viewing: <strong style={{ color: C.t1 }}>{ctxClientName || clientName}</strong>
            </span>
          )}
        </div>
      )}
      {/* Onboarding Checklist */}
      <OnboardingChecklist status={onboardingStatus} navigate={navigate} scanCount={SCAN_HISTORY_RAW.length} clientId={clientId} />

      {/* Row 1: Stat Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: '190px repeat(4,1fr)', gap: 12, marginBottom: 14 }}>
        <Card style={{ padding: '16px 12px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 5, borderTop: `2px solid ${scoreColor}` }}>
          {loading ? <Skeleton height={80} width={110} /> : <>
            <div style={{ fontSize: 9, color: C.t2, textTransform: 'uppercase', letterSpacing: '0.08em', fontFamily: FONTS.mono }}>Security Score</div>
            <ScoreGauge score={clientScore} delta={CLIENT?.delta} />
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4, marginTop: 2 }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: scoreColor }}>{clientScore == null ? 'Score Pending' : `${clientScore} / 100 - ${scoreLabel}`}</div>
              {scoreTrend !== 0 && <span style={{ fontSize: 10, fontWeight: 700, color: trendColor, fontFamily: FONTS.mono }}>{trendArrow} {Math.abs(scoreTrend)}</span>}
              {scoreTrend === 0 && clientScore != null && <span style={{ fontSize: 10, color: C.t3, fontFamily: FONTS.mono }}>{trendArrow}</span>}
            </div>
            <button
              type="button"
              onClick={() => setShowScoreMethodology(v => !v)}
              title="Security score methodology: a 0-100 model weighted by severity, exploit likelihood, exposure, asset criticality, and remediation status."
              style={{ marginTop: 4, background: 'none', border: 'none', color: C.sky, cursor: 'pointer', fontSize: 9, fontFamily: FONTS.sans, padding: 0 }}
            >
              What does this mean?
            </button>
            {showScoreMethodology && (
              <div style={{ marginTop: 6, padding: '7px 8px', borderRadius: 5, border: `1px solid ${C.b1}`, background: C.s2, color: C.t2, fontSize: 9, lineHeight: 1.35, textAlign: 'left' }}>
                0-100 weighted score based on severity, exploit likelihood, exposure, asset criticality, and remediation status.
              </div>
            )}
            {scoreCalcAt && <div style={{ fontSize: 8, color: C.t3, fontFamily: FONTS.mono, marginTop: 2 }}>Last calculated: {relativeTime(scoreCalcAt)}</div>}
            {scoreFallbackInfo && <div style={{ fontSize: 8, color: C.t3, fontFamily: FONTS.mono, marginTop: 2 }}>{scoreFallbackInfo}</div>}
          </>}
        </Card>
        {[
          { l: 'Open Critical/High', v: openCriticalHigh, sub: `${openCount} total open`, c: C.red, to: '/client/findings' },
          { l: 'Assets Monitored', v: totalAssets, sub: totalAssets > 0 ? [assetHosts && `${assetHosts} hosts`, assetDomains && `${assetDomains} domains`, assetAccounts && `${assetAccounts} accounts`, assetCloud && `${assetCloud} cloud`].filter(Boolean).join(', ') || `${totalAssets} assets` : 'Run a scan to populate', c: C.sky, to: '/client/assets' },
          { l: 'Dark Web Mentions', v: osintPresentation.breachRecords.value, sub: osintPresentation.coverageIncomplete ? `${breachRecords} confirmed record${breachRecords === 1 ? '' : 's'} · ${osintPresentation.coverageReason}` : exposedCredentials > 0 ? `${exposedCredentials} exposed credentials` : osintPresentation.breachRecords.note, c: osintPresentation.coverageIncomplete ? C.amb : C.pur, to: '/client/osint' },
          { l: 'Sensitive Data', v: sensitiveDataFindings, sub: openSensitiveDataFindings > 0 ? `${openSensitiveDataFindings} open groups` : 'No open sensitive-data groups', c: C.amb, to: '/client/sensitive-data' },
        ].map(s => (
          <Card key={s.l} onClick={() => navigate(s.to)} style={{ padding: '14px 18px', borderTop: `2px solid ${s.c}`, cursor: 'pointer' }}
            onMouseEnter={e => e.currentTarget.style.background = C.s2} onMouseLeave={e => e.currentTarget.style.background = C.s1}>
            {loading ? <><Skeleton height={10} width="55%" style={{ marginBottom: 8 }} /><Skeleton height={26} width="40%" /></> : <>
              <div style={{ fontSize: 9, color: C.t2, textTransform: 'uppercase', letterSpacing: '0.08em', fontFamily: FONTS.mono, marginBottom: 6 }}>{s.l}</div>
              <div style={{ fontSize: 42, fontWeight: 800, color: s.c, fontFamily: FONTS.mono, lineHeight: 1 }}>{s.v}</div>
              <div style={{ fontSize: 10, color: C.t2, marginTop: 4 }}>{s.sub}</div>
            </>}
          </Card>
        ))}
      </div>

      {/* Security Score Trend */}
      <div style={{ marginBottom: 14 }}><ScoreHistoryChart SCORE_HISTORY={SCORE_HISTORY} /></div>

      {/* Row 2: Recent Findings + Alerts/Breakdown */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 290px', gap: 12, marginBottom: 12 }}>
        <Card>
          <CardHeader title="Recent Findings" action={<Button variant="ghost" onClick={() => navigate('/client/findings')}>View all →</Button>} />
          {loading ? <div style={{ padding: 14, display: 'flex', flexDirection: 'column', gap: 8 }}>{[...Array(5)].map((_, i) => <Skeleton key={i} height={32} />)}</div> : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ borderCollapse: 'collapse', width: '100%' }}>
                <TH cols={['Finding', 'Severity', 'Status', 'EPSS', 'Age']} />
                <tbody>{groupByTitle(FINDINGS).slice(0, 6).map(g => {
                  const f = g.first;
                  return (
                    <TR key={g.key} onClick={() => navigate(`/client/findings/${f.id}`)}>
                      <TD>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                          <span style={{ fontSize: 12, color: C.t1, fontWeight: 500 }}>{f.title}</span>
                          {g.count > 1 && <span style={{ fontSize: 9, color: C.sky, fontFamily: FONTS.mono, fontWeight: 800 }}>({g.count})</span>}
                          {f.kev && <KevBadge />}
                        </div>
                        <div style={{ fontSize: 9, color: C.t2, fontFamily: FONTS.mono, marginTop: 1 }}>{targetLabel(f, g.count) || f.asset}</div>
                      </TD>
                      <TD><SeverityBadge value={g.sev} /></TD>
                      <TD><StatusBadge value={f.status} /></TD>
                      <TD><EpssVelocity direction={f.ep} score={f.ep} /></TD>
                      <TD><span style={{ fontSize: 10, color: C.t2, fontFamily: FONTS.mono }} title={f.age ? exactTime(f.age) : ''}>{f.age ? relativeTime(f.age) : '-'}</span></TD>
                    </TR>
                  );
                })}</tbody>
              </table>
            </div>
          )}
        </Card>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <Card>
            <CardHeader title="Alerts" action={<span style={{ fontSize: 9, background: C.redBg, color: C.red, borderRadius: 9999, padding: '2px 7px', fontFamily: FONTS.mono, border: `1px solid ${C.red}33` }}>2 new</span>} />
            {ALERTS.filter(a => a.client === CLIENT?.name).map(a => (
              <div key={a.id} onClick={() => navigate('/client/alerts')} style={{ padding: '9px 13px', borderBottom: `1px solid ${C.b2}`, background: !a.read ? C.redBg : 'transparent', display: 'flex', gap: 7, cursor: 'pointer' }}
                onMouseEnter={e => e.currentTarget.style.background = C.s2} onMouseLeave={e => e.currentTarget.style.background = !a.read ? C.redBg : 'transparent'}>
                <span style={{ fontSize: 11, marginTop: 1 }}>{a.severity === 'critical' ? '🔴' : '🟠'}</span>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 11, color: a.read ? C.t2 : C.t1, lineHeight: 1.4 }}>{a.msg}</div>
                  <div style={{ fontSize: 9, color: C.t3, fontFamily: FONTS.mono, marginTop: 1 }} title={exactTime(a.time)}>{relativeTime(a.time)}</div>
                </div>
                {!a.read && <div style={{ width: 5, height: 5, borderRadius: '50%', background: C.red, marginTop: 4, flexShrink: 0 }} />}
              </div>
            ))}
          </Card>

          <Card style={{ padding: 14 }}>
            <div style={{ fontSize: 12, fontWeight: 700, fontFamily: FONTS.sans, marginBottom: 10 }}>Finding Breakdown</div>
            {totalFindings === 0 ? (
              <div style={{ fontSize: 11, color: C.t3, fontFamily: FONTS.mono, textAlign: 'center', padding: '12px 0' }}>No findings yet</div>
            ) : [{ l: 'Critical', n: sevCounts.critical, c: C.red }, { l: 'High', n: sevCounts.high, c: C.org }, { l: 'Medium', n: sevCounts.medium, c: C.amb }, { l: 'Low', n: sevCounts.low, c: C.sky }, { l: 'Info', n: sevCounts.info, c: C.gry }].map(b => (
              <div key={b.l} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 7 }}>
                <div style={{ width: 50, fontSize: 9, color: C.t2, textAlign: 'right', fontFamily: FONTS.mono }}>{b.l}</div>
                <div style={{ flex: 1, height: 4, background: C.b1, borderRadius: 2, overflow: 'hidden' }}><div style={{ height: '100%', width: `${totalFindings > 0 ? (b.n / totalFindings) * 100 : 0}%`, background: b.c, borderRadius: 2 }} /></div>
                <div style={{ width: 22, fontSize: 11, fontWeight: 700, color: b.c, fontFamily: FONTS.mono, textAlign: 'right' }}>{b.n}</div>
              </div>
            ))}
          </Card>
        </div>
      </div>

      {/* Row 3: Attack Surface / Compliance / Attack Paths */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12, marginBottom: 12 }}>
        <Card>
          <CardHeader title="Attack Surface" action={<Button variant="ghost" onClick={() => navigate('/client/assets')}>Inventory →</Button>} />
          {totalAssets === 0 ? (
            <div style={{ padding: '20px 14px', textAlign: 'center', fontSize: 11, color: C.t3, fontFamily: FONTS.mono }}>No asset data yet. Run a scan to discover your attack surface.</div>
          ) : attackSurfaceRows.map(item => (
            <div key={item.l} onClick={() => navigate('/client/assets')} style={{ display: 'flex', alignItems: 'center', padding: '7px 13px', borderBottom: `1px solid ${C.b2}`, cursor: 'pointer' }}
              onMouseEnter={e => e.currentTarget.style.background = C.s2} onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 12, color: item.c, fontFamily: FONTS.sans }}>{item.l}</div>
                <div style={{ fontSize: 9, color: item.signal.color, marginTop: 1 }}>{item.signal.text}</div>
              </div>
              <span style={{ fontSize: 13, fontWeight: 700, color: item.c, fontFamily: FONTS.mono }}>{item.n}</span>
            </div>
          ))}
        </Card>

        <Card style={{ padding: 14 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <div style={{ fontSize: 12, fontWeight: 700, fontFamily: FONTS.sans }}>Compliance Posture</div>
            <Button variant="ghost" onClick={() => navigate('/client/compliance')}>Details →</Button>
          </div>
          {COMP.map(item => {
            const pending = item.s == null || item.c === 0 || item.status === 'pending' || item.status === 'untested';
            const col = pending ? C.t3 : item.s >= 75 ? C.grn : item.s >= 50 ? C.amb : C.red;
            return (
              <div key={item.n} style={{ marginBottom: 8 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 3 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
                    <span style={{ fontSize: 8, fontWeight: 800, color: C.sky, fontFamily: FONTS.mono, background: `${C.sky}14`, border: `1px solid ${C.sky}33`, borderRadius: 3, padding: '1px 5px', lineHeight: 1.5 }}>{item.mark}</span>
                    <span style={{ fontSize: 10, color: C.t1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{item.label}</span>
                  </div>
                  <div style={{ display: 'flex', gap: 5 }}>
                    <span style={{ fontSize: 11, fontWeight: 700, color: col, fontFamily: FONTS.mono }}>{pending ? 'Pending' : `${item.s}%`}</span>
                    {!pending && item.d !== 0 && <span style={{ fontSize: 9, color: item.d > 0 ? C.grn : C.red, fontFamily: FONTS.mono }}>{item.d > 0 ? '+' : ''}{item.d}%</span>}
                  </div>
                </div>
                <div style={{ height: 3, background: C.b1, borderRadius: 2, overflow: 'hidden' }}><div style={{ height: '100%', width: `${pending ? 0 : item.s}%`, background: col, borderRadius: 2 }} /></div>
              </div>
            );
          })}
        </Card>

        <Card style={{ minHeight: 200 }}>
          <CardHeader title="Attack Paths" action={
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <span style={{ fontSize: 9, color: C.red, fontFamily: FONTS.mono }}>{numberAt(valueAt(metricsRollup, 'attackChains') ?? {}, ATTACK_PATHS.length, 'active')} active paths</span>
              <Button variant="ghost" onClick={() => navigate('/client/attack-paths')}>View all →</Button>
            </div>
          } />
          <div style={{ padding: 0 }}>
            {ATTACK_PATHS.filter(Boolean).slice(0, 4).map(p => {
              const pId = p.pathId ?? p.id ?? '';
              const pSev = normalizeSeverity(p.severity ?? p.sev).toLowerCase();
              const pName = p.name ?? p.title ?? '';
              const pProb = p.prob ?? p.exploitProbability ?? 0;
              return (
              <div key={pId} onClick={() => navigate(`/client/attack-paths/${pId}`)} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '9px 14px', borderBottom: `1px solid ${C.b2}`, cursor: 'pointer', transition: 'background 0.12s' }}
                onMouseEnter={e => e.currentTarget.style.background = C.s2} onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                <SeverityBadge value={pSev} />
                <span style={{ flex: 1, fontSize: 10, color: C.t1, fontFamily: FONTS.mono }}>{pName}</span>
                <div style={{ textAlign: 'right' }}>
                  <span style={{ fontSize: 11, fontWeight: 700, color: pProb >= 70 ? C.red : C.org, fontFamily: FONTS.mono }}>{pProb}%</span>
                  <div style={{ fontSize: 7, color: C.t3, fontFamily: FONTS.mono }}>EXPLOIT PROB</div>
                </div>
              </div>
              );
            })}
          </div>
        </Card>
      </div>

      {/* Row 4: OSINT Exposure Center */}
      <Card style={{ marginBottom: 12 }}>
        <CardHeader title="OSINT Exposure Center" action={
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            {clientLastScan && <span style={{ fontSize: 9, color: C.t2, fontFamily: FONTS.mono }}>Last scan {relativeTime(clientLastScan)}</span>}
            <Button variant="ghost" onClick={() => navigate('/client/osint')}>Full Report →</Button>
          </div>
        } />
        {osintPresentation.coverageIncomplete && (
          <div style={{ padding: '9px 18px', background: `${C.amb}12`, borderTop: `1px solid ${C.amb}44`, borderBottom: `1px solid ${C.amb}44`, color: C.amb, fontSize: 10, fontFamily: FONTS.mono }}>
            Coverage incomplete: {osintPresentation.coverageReason}. Zero breach and credential values are unverified.
          </div>
        )}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr' }}>
          {[
            { l: 'Breach Records', v: osintPresentation.breachRecords.value, c: osintPresentation.coverageIncomplete ? C.amb : breachRecords > 0 ? C.red : C.grn, note: osintPresentation.breachRecords.note },
            { l: 'Exposed Credentials', v: osintPresentation.exposedCredentials.value, c: osintPresentation.coverageIncomplete ? C.amb : exposedCredentials > 0 ? C.org : C.grn, note: osintPresentation.exposedCredentials.note },
            { l: 'Exposed Services', v: osintPresentation.exposedServices.value, c: osintPresentation.coverageIncomplete ? C.amb : exposedServices > 0 ? C.amb : C.grn, note: osintPresentation.exposedServices.note },
            { l: 'Total OSINT Findings', v: osintPresentation.totalExposureFindings.value, c: osintPresentation.coverageIncomplete ? C.amb : totalOsintFindings > 0 ? C.sky : C.grn, note: osintPresentation.totalExposureFindings.note },
          ].map((item, i) => (
            <div key={item.l} onClick={() => navigate('/client/osint')} style={{ padding: '16px 18px', borderRight: i < 3 ? `1px solid ${C.b1}` : 'none', cursor: 'pointer' }}
              onMouseEnter={e => e.currentTarget.style.background = C.s2} onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
              <div style={{ fontSize: 9, color: C.t2, textTransform: 'uppercase', letterSpacing: '0.08em', fontFamily: FONTS.mono, marginBottom: 6 }}>{item.l}</div>
              <div style={{ fontSize: 26, fontWeight: 800, color: item.c, fontFamily: FONTS.mono, lineHeight: 1, marginBottom: 3 }}>{item.v}</div>
              <div style={{ fontSize: 10, color: C.t2 }}>{item.note}</div>
            </div>
          ))}
        </div>
      </Card>

      {/* Executive Summary */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 12, marginBottom: 12 }}>
        {[
          { l: 'Security Score', v: clientScore != null ? String(clientScore) : '\u2014', delta: scoreTrend !== 0 ? `${trendArrow} ${scoreTrend > 0 ? '+' : ''}${scoreTrend} pts` : '', deltaColor: trendColor, c: scoreColor, spark: SCORE_HISTORY.map(d => d?.scoreValue ?? d?.org ?? 0).slice(-8), sub: clientScore != null ? scoreLabel : scanList.length > 0 ? `${scanList.length} scans complete` : 'Pending first scan' },
          { l: 'Total Findings', v: String(totalFindings), delta: '', c: totalFindings > 0 ? C.t1 : C.t3, spark: [], sub: `${openCount} open` },
          { l: 'Resolved', v: String(resolvedCount), delta: '', c: resolvedCount > 0 ? C.grn : C.t3, spark: [], sub: resolvedCount > 0 ? 'Remediated' : scanList.length > 0 ? 'Open remediation plan' : 'None yet' },
          { l: 'Open Critical Findings', v: String(sevCounts.critical), delta: '', c: sevCounts.critical > 0 ? C.red : C.grn, spark: [], sub: sevCounts.critical > 0 ? 'Needs attention' : 'All clear' },
        ].map(s => (
          <Card key={s.l} style={{ padding: '14px 16px', borderTop: `2px solid ${s.c}` }}>
            <div style={{ fontSize: 9, color: C.t2, textTransform: 'uppercase', letterSpacing: '0.08em', fontFamily: FONTS.mono, marginBottom: 6 }}>{s.l}</div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: 4 }}>
              <div>
                <div style={{ fontSize: 26, fontWeight: 800, color: s.c, fontFamily: FONTS.mono, lineHeight: 1 }}>{s.v}</div>
                {s.delta && <div style={{ fontSize: 10, color: s.deltaColor || C.grn, fontFamily: FONTS.mono, marginTop: 2 }}>{s.delta}</div>}
              </div>
              <Sparkline data={s.spark} color={s.c} />
            </div>
            <div style={{ fontSize: 10, color: C.t2 }}>{s.sub}</div>
          </Card>
        ))}
      </div>

      {/* Risk Summary + Remediation Velocity */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
        <Card>
          <CardHeader title="Risk Summary" />
          <div style={{ padding: 16 }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 14 }}>
              {[
                { l: 'Total Findings', v: totalFindings, c: totalFindings > 0 ? C.t1 : C.t3 },
                { l: 'Open', v: openCount, c: openCount > 0 ? C.org : C.t3 },
                { l: 'Remediated', v: resolvedCount, c: resolvedCount > 0 ? C.grn : C.t3 },
                { l: 'Risk Accepted', v: acceptedCount, c: acceptedCount > 0 ? C.gry : C.t3 },
              ].map(r => (
                <div key={r.l} style={{ padding: 10, background: C.s2, borderRadius: 6, border: `1px solid ${C.b1}` }}>
                  <div style={{ fontSize: 18, fontWeight: 800, color: r.c, fontFamily: FONTS.mono }}>{r.v}</div>
                  <div style={{ fontSize: 9, color: C.t2, fontFamily: FONTS.mono, marginTop: 2 }}>{r.l}</div>
                </div>
              ))}
            </div>
            {totalFindings > 0 && (<>
              <div style={{ fontSize: 10, fontWeight: 600, fontFamily: FONTS.sans, marginBottom: 8, color: C.t1 }}>Severity Distribution</div>
              {[{ l: 'Critical', n: sevCounts.critical, c: C.red }, { l: 'High', n: sevCounts.high, c: C.org }, { l: 'Medium', n: sevCounts.medium, c: C.amb }].map(b => (
                <div key={b.l} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                  <div style={{ width: 50, fontSize: 9, color: C.t2, textAlign: 'right', fontFamily: FONTS.mono }}>{b.l}</div>
                  <div style={{ flex: 1, height: 5, background: C.b1, borderRadius: 3, overflow: 'hidden' }}><div style={{ height: '100%', width: `${openCount > 0 ? (b.n / openCount) * 100 : 0}%`, background: b.c, borderRadius: 3 }} /></div>
                  <div style={{ width: 20, fontSize: 11, fontWeight: 700, color: b.c, fontFamily: FONTS.mono, textAlign: 'right' }}>{b.n}</div>
                </div>
              ))}
            </>)}
            {totalFindings === 0 && (
              <div style={{ fontSize: 11, color: C.t3, fontFamily: FONTS.mono, textAlign: 'center', padding: '8px 0' }}>Pending first scan</div>
            )}
          </div>
        </Card>

        <Card>
          <CardHeader title="Remediation Velocity" />
          <div style={{ padding: 16, textAlign: 'center' }}>
            <div style={{ fontSize: 11, color: C.t2, fontFamily: FONTS.mono, lineHeight: 1.7, padding: '20px 12px' }}>
              Remediation tracking will populate after findings are acknowledged and resolved. Velocity metrics require at least 30 days of finding history.
            </div>
            {resolvedCount > 0 && (
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 0', borderTop: `1px solid ${C.b1}`, marginTop: 4 }}>
                <span style={{ fontSize: 10, color: C.t2 }}>Resolved to date</span>
                <span style={{ fontSize: 12, fontWeight: 700, color: C.grn, fontFamily: FONTS.mono }}>{resolvedCount} findings</span>
              </div>
            )}
          </div>
        </Card>
      </div>

      {/* QBR + Strategic Recommendations */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
        <Card>
          <CardHeader title="Quarterly Business Review" action={qbrReports.length > 0 ? <Button variant="pri" style={{ fontSize: 10 }} onClick={() => navigate('/client/reports')}>View Reports</Button> : null} />
          <div style={{ padding: 16, textAlign: 'center' }}>
            <div style={{ fontSize: 11, color: C.t2, fontFamily: FONTS.mono, lineHeight: 1.7, padding: '16px 12px' }}>
              {latestQbr
                ? `${latestQbr.title ?? latestQbr.name ?? 'Quarterly Business Review'} is ready${latestQbr.generatedAt ?? latestQbr.gen ? ` from ${new Date(latestQbr.generatedAt ?? latestQbr.gen).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}` : ''}.`
                : 'Quarterly Business Review reports are generated automatically after onboarding.'}
            </div>
            {qbrReports.length > 0 && (
              <Button variant="sec" onClick={() => navigate('/client/reports')}>View Available Reports →</Button>
            )}
          </div>
        </Card>

        <Card>
          <CardHeader title="Top Recommendations" action={totalFindings > 0 ? <Button variant="ghost" onClick={() => navigate('/client/findings')}>View all →</Button> : null} />
          <div style={{ padding: 16 }}>
            {(() => {
              const topFindings = groupByTitle(FINDINGS.filter(f => String(f.status ?? '').toLowerCase() === 'open'))
                .map(group => {
                  const impact = Math.max(...group.findings.map(f => recommendationScore(f)));
                  return { group, finding: group.first, impact };
                })
                .sort((a, b) => b.impact - a.impact)
                .slice(0, 5);
              if (topFindings.length === 0) return (
                <div style={{ fontSize: 11, color: C.t3, fontFamily: FONTS.mono, textAlign: 'center', padding: '16px 0' }}>
                  No open findings to recommend actions for. Run a scan to get started.
                </div>
              );
              const sevColors = { critical: C.red, high: C.org, medium: C.amb, low: C.sky, info: C.gry };
              return topFindings.map(({ group, finding: f, impact }, i) => {
                const sev = normalizeSeverity(f.sev ?? f.severity).toLowerCase();
                return (
                <div key={group.key} onClick={() => navigate(`/client/findings/${f.id}`)} style={{ padding: '8px 0', borderBottom: `1px solid ${C.b2}`, cursor: 'pointer' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 3 }}>
                    <span style={{ fontSize: 9, fontWeight: 800, color: sevColors[sev] || C.t2, fontFamily: FONTS.mono, background: `${sevColors[sev] || C.t2}15`, padding: '1px 5px', borderRadius: 3, border: `1px solid ${sevColors[sev] || C.t2}33` }}>P{i + 1}</span>
                    <span style={{ fontSize: 11, fontWeight: 600, color: C.t1, fontFamily: FONTS.sans }}>{f.title}</span>
                    {group.count > 1 && <span style={{ fontSize: 9, color: C.t3, fontFamily: FONTS.mono }}>({group.count})</span>}
                  </div>
                  <div style={{ fontSize: 10, color: C.t2, lineHeight: 1.4, marginBottom: 2 }}>{friendlyFindingSource(f)}</div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <SeverityBadge value={sev} />
                    <span style={{ fontSize: 9, color: C.t3 }}>{recommendationReason(f, impact)}</span>
                  </div>
                </div>
                );
              });
            })()}
          </div>
        </Card>
      </div>

      {/* Quick Actions: Scan + Setup Package */}
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      <Card style={{ padding: 16, marginBottom: 12, borderLeft: `3px solid ${C.sky}` }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10 }}>
          <div>
            <div style={{ fontSize: 13, fontWeight: 700, fontFamily: FONTS.sans, marginBottom: 3 }}>Quick Actions</div>
            <div style={{ fontSize: 10, color: C.t2 }}>Run scans or download client setup files</div>
            <div style={{ fontSize: 10, color: C.t2, marginTop: 3 }}>
              {(isPlatformAdmin || isMspUser)
                ? 'Use Endpoint Connector ZIP for workstation deployment; General Setup ZIP contains environment guidance.'
                : 'For workstation deployment, request the Endpoint Connector ZIP from your MSP.'}
            </div>
            {clientLastScan && <div style={{ fontSize: 9, color: C.t3, fontFamily: FONTS.mono, marginTop: 3 }}>Last scanned: {relativeTime(clientLastScan)}</div>}
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {(isPlatformAdmin || isMspUser) && <Button variant="pri" onClick={handleTriggerScan} disabled={scanning || !clientId}>
              {scanning ? (<><span style={{ display: 'inline-block', width: 12, height: 12, border: '2px solid rgba(255,255,255,0.3)', borderTopColor: '#fff', borderRadius: '50%', animation: 'spin 0.8s linear infinite', marginRight: 6, verticalAlign: 'middle' }} />Scan in Progress...</>) : 'Run Full Scan'}
            </Button>}
            {(isPlatformAdmin || isMspUser) && <Button variant="pri" onClick={handleDownloadEndpointPackage} disabled={downloadingEndpointPkg || !clientId}>
              {downloadingEndpointPkg ? 'Generating...' : 'Download Endpoint Connector ZIP'}
            </Button>}
            <Button variant="sec" onClick={handleDownloadPackage} disabled={downloadingPkg || !clientId}>
              {downloadingPkg ? 'Downloading...' : 'Download General Setup ZIP'}
            </Button>
          </div>
        </div>
        {scanMessage && (
          <div style={{ marginTop: 10, padding: '8px 12px', borderRadius: 6, fontSize: 11, fontFamily: FONTS.mono, background: scanMessage.startsWith('Failed') ? C.redBg : `${C.grn}12`, border: `1px solid ${scanMessage.startsWith('Failed') ? C.red : C.grn}33`, color: scanMessage.startsWith('Failed') ? C.red : C.grn }}>
            {scanMessage}
          </div>
        )}
      </Card>

      {/* Export & Schedule */}
      <Card style={{ padding: 16, marginBottom: 12 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <div style={{ fontSize: 13, fontWeight: 700, fontFamily: FONTS.sans, marginBottom: 3 }}>Export & Schedule</div>
            <div style={{ fontSize: 10, color: C.t2 }}>Generate board-ready reports and schedule recurring QBR deliveries</div>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <Button variant="pri" onClick={() => navigate('/client/reports')}>View Reports</Button>
            <Button variant="sec" onClick={() => navigate('/client/reports')}>Schedule QBR</Button>
          </div>
        </div>
      </Card>

      {/* Recent Reports */}
      <Card style={{ marginTop: 12 }}>
        <CardHeader title="Recent Reports" action={<Button variant="ghost" onClick={() => navigate('/client/reports')}>View all →</Button>} />
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)' }}>
          {dedupedReports.slice(0, 4).map((r, i) => {
            const rId = r.reportId ?? r.id ?? i;
            const rType = (r.reportType ?? r.type ?? 'monthly').toString().toLowerCase().replace(/\d+/g, '');
            const rName = r.title ?? r.name ?? `${rType} Report`;
            const rGen = r.generatedAt ?? r.gen ?? null;
            const rSize = r.sizeBytes ? `${Math.round(r.sizeBytes / 1024)} KB` : null;
            const icons = { monthly: '📊', qbr: '📋', compliance: '✅', pentest: '🔐' };
            const typeLabels = { monthly: 'Monthly Report', qbr: 'QBR Deck', compliance: 'Compliance', pentest: 'Pentest Report' };
            const typeColors = { monthly: C.sky, qbr: C.pur, compliance: C.grn, pentest: C.red };
            return (
              <div key={rId} onClick={() => navigate('/client/reports')} style={{
                padding: '16px 18px', borderRight: i < 3 ? `1px solid ${C.b1}` : 'none',
                cursor: 'pointer', transition: 'background 0.12s',
              }} onMouseEnter={e => e.currentTarget.style.background = C.s2}
                 onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                <div style={{ fontSize: 20, marginBottom: 8 }}>{icons[rType] || '📄'}</div>
                <div style={{ fontSize: 11, fontWeight: 600, color: C.t1, fontFamily: FONTS.sans, marginBottom: 4, lineHeight: 1.3 }}>{rName}</div>
                <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginBottom: 4 }}>
                  <span style={{ fontSize: 8, color: typeColors[rType] || C.sky, fontFamily: FONTS.mono, textTransform: 'uppercase', letterSpacing: '0.06em', background: `${typeColors[rType] || C.sky}14`, padding: '2px 6px', borderRadius: 3 }}>{typeLabels[rType] || rType}</span>
                </div>
                <div style={{ fontSize: 9, color: C.t3, fontFamily: FONTS.mono }}>{rGen ? relativeTime(rGen) : '-'}{rSize ? ` / ${rSize}` : ''}</div>
              </div>
            );
          })}
        </div>
      </Card>
    </Shell>
  );
}
